import { ThemeComponent } from "@/theme/ThemeComponent";
import { CartItem, Component, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { CartItemRow } from "./CartItemRow";
import { cart } from "@/theme/Cart";
import { QuantitySpinner } from "../QuantitySpinner/QuantitySpinner";
import { formatPrice} from "@/utils/products";

@Component
export class CartMain extends ThemeComponent {
    @Ref() checkoutButton!: HTMLAnchorElement;
    @Ref() emptyCart!: HTMLElement;
    @Ref({required: false}) promoBanner!: HTMLElement;
    @Ref() items!: HTMLElement;
    @Ref() count!: HTMLElement;
    @Ref() subtotal!: HTMLElement;

    protected strings = this.dataset.strings? this.dataset.strings: '';
    private itemCount: number = 0;
    private cartSubtotal: number = 0;
    private hasSubscription: boolean = false;
    private loading: boolean = true;
    private cartNeedsRefresh: boolean = true;

    private _onCartCountChange = new SimpleEvent<number>();
    public get onCartCountChange() {
        return this._onCartCountChange.asEvent();
    }

    /**
     * Initialise.
     */
    init() {
        this.setLoading();

        this.$subscribe(cart.onChange, () => {
            if (!this.loading) {
                this.cartNeedsRefresh = true;
                void this.cartUpdated();
                this.removeLoading();
            }

        });

        this.removeLoading();
    }

    removeLoading() {
        this.classList.remove('cart-loading');
        this.checkoutButton.classList.remove('disabled');
        this.loading = false;

    }

    setLoading() {
        this.classList.add('cart-loading');
        this.checkoutButton.classList.add('disabled');
        this.loading = true;
    }

    /**
     * Update item count.
     * @param count - count
     */
    updateCount(count: number) {
        this.count.innerHTML = String(count);

        this._onCartCountChange.dispatch(count);
    }

    /**
     * Update item count.
     * @param count - count
     */
    updateSubtotal(cartSubtotal:number) {

        // If there are upsell products in cart we need
        // their 'discounts' to be factored in to the
        // displayed subtotal

        const cartItems = cart.items;
        const upsellItems = cartItems.filter((item) => {
            if (item.properties && item.properties['_upsellDiscountAmount']) {
                return item.properties['_upsellDiscountAmount'];
            }
        });

        if (upsellItems.length) {
            const upsellItemDiscounts = upsellItems.map((item) => {
                if (
                    item.properties &&
                    item.properties['_upsellDiscountAmount']
                ) {
                    return Number(item.properties['_upsellDiscountAmount']);
                } else {
                    return 0;
                }
            });
            const upsellTotalDiscount = upsellItemDiscounts?.reduce(
                (prevItem, currentItem) => Number(prevItem) + Number(currentItem));

            cartSubtotal = cartSubtotal - Number(upsellTotalDiscount);
        }

        const formattedSubtotal = formatPrice(cartSubtotal, String(JSON.parse(this.strings).moneyFormat));
        if (formattedSubtotal) {
            this.subtotal.innerHTML = formattedSubtotal;
        }
        this.subtotal.setAttribute('data-subtotal-amount', String(cartSubtotal));
    }

    /**
     * Update Cart
     * @param container - items container.
     * @param emptyCart - cart empty message.
     * @returns
     */
    cartUpdated() {
        if (this.loading || !this.cartNeedsRefresh) {
            return true;
        }
        this.setLoading();
        this.hasSubscription = false;

        const itemsLength = cart.items.length;


        let length = 0;

        if (itemsLength <= 0) {
            this.items.innerHTML = '';
            this.closest('.cart')?.classList.add('cart--empty');
            this.emptyCart.style.display = 'block';
            this.checkoutButton.style.display ='none';
            this.updateCount(0);
            this.updateSubtotal(0);
            return;
        }

        this.closest('.cart')?.classList.remove('cart--empty');
        this.emptyCart.style.display = 'none';
        this.checkoutButton.style.display ='block';
        this.itemCount = 0;
        this.cartSubtotal = 0;
        const rows = [];

        for (const item of cart.items) {
            const row = new CartItemRow(item, new QuantitySpinner(1 , 99, item.quantity), this.strings);

            this.cartSubtotal += item.finalLinePrice;

            void this.checkForFreeGift(item);

            // Recharge products show a different quantity to the user
            // so we need to set the quantity that the user
            // sees as a property which we can then display.
            // See also ProductForm.ts, where the quantity is set
            // for each item

            if (
                item.properties &&
                item.properties['shipping_interval_frequency'] &&
                item.properties['shipping_interval_frequency'] != ''
            ) {
                this.hasSubscription = true;
            }

            const quantity = item.properties && item.properties['_display_quantity'] ?
                item.quantity / Number(item.properties['_display_quantity']) :
                item.quantity;

            this.itemCount += quantity; length++;

            rows.push(row);

            if (length < itemsLength) { continue; }

            this.updateCount(this.itemCount);
            this.updateSubtotal(this.cartSubtotal);
        }

        this.items.innerHTML = '';

        for (const item_row of rows) {
            this.items.append(item_row);
        }

        if (this.hasSubscription) {
            this.checkoutButton.setAttribute('data-disable-recharge', 'false');
        } else {
            this.checkoutButton.setAttribute('data-disable-recharge', 'true');
        }

        this.removeLoading();
        this.cartNeedsRefresh = false;
    }

    // Check for free gift at product level instead of global/theme settings.
    async checkForFreeGift(eligibleItem:CartItem) {
        let eligibleProductId: number | null = null;
        const checkEligibleItem = eligibleItem.properties['_eligibleProductId'];
        let purchasedQty = 0;
        if (checkEligibleItem) {
            eligibleProductId = Number(checkEligibleItem);
        } else {
            return;
        }

        const giftExists: unknown[] = [];
        let i = 0;

        // Loop through all products in cart on each product & see if the eligibleGiftId matches.
        for (const item of cart.items) {
            giftExists[i] = false;

            if(eligibleProductId !== null && eligibleProductId === item.id){
                giftExists[i] = true;
                purchasedQty = item.properties && item.properties['_display_quantity'] ?
                    item.quantity / Number(item.properties['_display_quantity']) :
                    item.quantity;
            }

            i++;
        }

        if (eligibleProductId && giftExists.every((v) => !v)) {
            await cart.removeItem(eligibleItem).catch((err) => {
                throw new Error(err);
            });
        }
        if (purchasedQty !== eligibleItem.quantity) {
            await cart.changeItem(
                eligibleItem,
                Number(purchasedQty),
                null
            ).catch((err) => {
                throw new Error(err);
            });
        }
    }

}

declare global {
    interface Window {
        reChargeCartJS?(): null;
    }
}
